/**
 * Every query key in the app, in one place.
 *
 * Settled before the first screen exists so the M3 mutations know exactly what
 * to invalidate. Keys invented ad hoc per screen are how a dashboard ends up
 * showing a stale row after a write: the mutation invalidates
 * ["transactions"] while the list is actually cached under ["transaction-list"]
 * and nothing refetches.
 *
 * The convention is `[domain]` for a list and `[domain-singular, id]` for one
 * record, with any filter object as the list's second element. Invalidating
 * `keys.transactions.all` therefore catches every filtered variant at once,
 * because TanStack Query matches keys by prefix.
 */

export type Filters = Record<string, unknown> | undefined;

function collection<const TAll extends string, const TOne extends string>(
  all: TAll,
  one: TOne,
) {
  return {
    all: [all] as const,
    list: (filters?: Filters) => [all, filters ?? {}] as const,
    detail: (id: string) => [one, id] as const,
  };
}

export const keys = {
  /** The signed-in staff member. Drives the _authed gate. */
  me: ["me"] as const,

  transactions: collection("transactions", "transaction"),
  storefronts: collection("storefronts", "storefront"),
  /** The counterparty above storefronts (PRO-993). */
  merchants: collection("merchants", "merchant"),
  /** One merchant's pricing windows, keyed by merchant id (PRO-994/1200). */
  feeSchedules: collection("fee-schedules", "fee-schedule"),
  /** Merchant x method pairs with no schedule to price their next settlement
   *  (PRO-1202). One list, no detail. */
  feeGaps: collection("fee-gaps", "fee-gap"),
  webhookEvents: collection("webhook-events", "webhook-event"),
  /** Late payments Altura refused to apply (PRO-1345). One list, no detail. */
  paymentConflicts: collection("payment-conflicts", "payment-conflict"),
  /** Keyed by order id. There is no list form: a timeline is always one
   *  order's, merged server-side from three tables. */
  timeline: collection("timelines", "timeline"),
  callbacks: collection("callbacks", "callback"),
  /** One storefront's callback settings, keyed by storefront id. Separate from
   *  `storefronts` because a config write and a storefront write invalidate
   *  different things, and separate from `callbacks` because that is the
   *  delivery queue rather than the configuration behind it. */
  callbackConfig: collection("callback-configs", "callback-config"),
  settlements: collection("settlements", "settlement"),
  disbursements: collection("disbursements", "disbursement"),
  /** One merchant's settlement statements (PRO-997, screens PRO-1400), keyed
   *  by merchant id for the list and by invoice id for one document. */
  invoices: collection("invoices", "invoice"),
  /** One merchant's balance and journals, keyed by merchant id. Every
   *  statement and payout write moves it. */
  merchantLedger: collection("merchant-ledgers", "merchant-ledger"),
  bridgeOrders: collection("bridge-orders", "bridge-order"),
  auditLog: collection("audit-log", "audit-log-entry"),
  apiKeys: collection("api-keys", "api-key"),
  shopifyCredentials: collection("shopify-credentials", "shopify-credential"),
  /** Global search, keyed by the query string. There is no detail form: a
   *  search result IS a pointer to some other collection's detail. */
  search: collection("search", "search-result"),
} as const;
